/**
 * @fileoverview Utility for loading test scenarios into the Zodiac engine.
 *
 * @llm IMPORTANT: Keeps scenario/test logic out of the core engine.
 *
 * ARCHITECTURAL GUIDELINES:
 * - Pure Functions: This file should only contain pure functions with no side effects
 * - No UI logic: Only interacts with the engine and its managers
 * - No direct mutation: Uses engine methods to update state
 *
 * RESPONSIBILITIES:
 * - Loads a FEN scenario into the engine for Testing AI Heuristics and why its so easy to exploit at the moment!
 * - We want to focus on the tactical heuristic and adapting it to be more robust to these scenarios.
 * - Uses the EXACT same parsing logic as ZodiacFEN.decode to ensure test scenarios work correctly
 *
 * NOTE ON INDEXING:
 * - Vertical axis uses reverse indexing (0 at top, 7 at bottom)
 * - Horizontal axis uses regular indexing (0 to 4 from left to right)
 *
 * WEAKNESSES:
 * Defending against RACE victory
 *
 */

import { ZodiacEngine } from "../zodiacEngine";
import { Mode, State, Position, GameState } from "../types";
import { AIController } from "../AIController";
import { PieceType } from "../types";

// Default hardcoded test scenario FEN (can be changed as needed)
// Now uses the exact same FEN format as the actual game - no more upside-down scenarios!
export const TEST_SCENARIO_FEN = "8/R*7/1mk6/2MK5/8";

/**
 * Helper function to detect if we're in a simple Rules demonstration context
 */
function isSimpleRulesDemo(
  gameState: GameState,
  engine: ZodiacEngine
): boolean {
  // Count total pieces on the board
  let totalPieces = 0;
  for (let row = 0; row < gameState.board.length; row++) {
    for (let col = 0; col < gameState.board[row].length; col++) {
      totalPieces += gameState.board[row][col].Pieces.length;
    }
  }

  // Rules demos typically have very few pieces (1-5) compared to full games
  // Also check if turnCount is 0 (fresh demo scenario)
  const hasVeryFewPieces = totalPieces <= 5;
  const isNewScenario = gameState.turnCount === 0;

  return hasVeryFewPieces && isNewScenario;
}

/**
 * Rebuilds entanglement relationships after loading from FEN
 * FEN only stores the isEntangled flag on pieces, not the octopus's entangledPieceId
 * This function restores the bidirectional relationship for multiplayer sync
 */
function rebuildEntanglementRelationships(gameState: GameState): void {

  const diagonalDirections = [
    [-1, -1], // Top Left
    [-1, 1], // Top Right
    [1, 1], // Bottom Right
    [1, -1], // Bottom Left
  ];

  let rebuiltCount = 0;

  // Find all octopi and check for adjacent entangled pieces
  for (let row = 0; row < gameState.board.length; row++) {
    for (let col = 0; col < gameState.board[row].length; col++) {
      const square = gameState.board[row][col];

      for (const piece of square.Pieces) {
        // Skip if not an octopus
        if (piece.type !== PieceType.Octopus) continue;

        // Check diagonally adjacent squares for entangled pieces
        for (const [dRow, dCol] of diagonalDirections) {
          const adjRow = row + dRow;
          const adjCol = col + dCol;

          // Check bounds
          if (
            adjRow < 0 ||
            adjRow >= gameState.board.length ||
            adjCol < 0 ||
            adjCol >= gameState.board[0].length
          ) {
            continue;
          }

          const adjSquare = gameState.board[adjRow][adjCol];

          // Check for entangled pieces in this square
          for (const adjPiece of adjSquare.Pieces) {
            if (adjPiece.isEntangled && adjPiece.isLight !== piece.isLight) {
              // Found an entangled enemy piece adjacent to this octopus!
              piece.entangledPieceId = adjPiece.id;
              rebuiltCount++;
            }
          }
        }
      }
    }
  }

}

/**
 * Rebuilds virus infection relationships after loading from FEN
 * When a Virus is stacked with another piece via "+V" notation,
 * the other piece needs to have isInfected = true set
 */
function rebuildVirusInfectionRelationships(gameState: GameState): void {

  let rebuiltCount = 0;

  // Scan all squares for virus stacking
  for (let row = 0; row < gameState.board.length; row++) {
    for (let col = 0; col < gameState.board[row].length; col++) {
      const square = gameState.board[row][col];

      // Check if this square has a Virus
      const hasVirus = square.Pieces.some((p) => p.type === PieceType.Virus);

      if (hasVirus && square.Pieces.length > 1) {
        // Mark all non-Virus pieces on this square as infected
        for (const piece of square.Pieces) {
          if (piece.type !== PieceType.Virus && !piece.isInfected) {
            piece.isInfected = true;
            rebuiltCount++;
          }
        }
      }
    }
  }

}

/**
 * Rebuilds housing relationships after loading from FEN
 * When a climbing piece shares a square with a Tree via "+" notation,
 * the climbing piece needs isHousing = true set.
 * This provides backward compatibility with FEN strings created before
 * the 'h' marker was added to the encoder.
 */
function rebuildHousingRelationships(gameState: GameState): void {
  let rebuiltCount = 0;

  for (let row = 0; row < gameState.board.length; row++) {
    for (let col = 0; col < gameState.board[row].length; col++) {
      const square = gameState.board[row][col];
      if (square.Pieces.length < 2) continue;

      const hasTree = square.Pieces.some((p) => p.type === PieceType.Tree);
      if (!hasTree) continue;

      // Find climbing pieces (non-Tree, non-Virus on same square as Tree)
      for (const piece of square.Pieces) {
        if (piece.type !== PieceType.Tree && piece.type !== PieceType.Virus && !piece.isHousing) {
          piece.isHousing = true;
          rebuiltCount++;
        }
      }
    }
  }

  if (rebuiltCount > 0) {
  }
}

// Force recompile trigger
export function FenToGameState(
  fenString: string,
  engine: ZodiacEngine
): GameState {
  const gameState = engine.getGameState(); // Get a clean game state

  // Check for bonus turn piece ID suffix
  let bonusTurnPieceId: string | null = null;
  const bonusIndex = fenString.indexOf("~");
  if (bonusIndex !== -1) {
    // Extract piece ID after "~:"
    bonusTurnPieceId = fenString.substring(bonusIndex + 2);
    // Remove everything from "~" onwards
    fenString = fenString.substring(0, bonusIndex);
  }

  // MULTIPLAYER FIX: Check for turn count metadata suffix (@{turnCount})
  const turnMetadataIndex = fenString.indexOf("@");
  if (turnMetadataIndex !== -1) {
    // Just remove the turn metadata - we don't need to parse it here
    // The turn count is managed by the engine's state
    fenString = fenString.substring(0, turnMetadataIndex);
  }

  // Check if it's dark's turn
  const isDarkTurn = fenString.startsWith("d");
  if (isDarkTurn) {
    fenString = fenString.substring(1);
    gameState.isLightTurn = false;
  } else {
    gameState.isLightTurn = true;
  }

  // Split into columns
  const columns = fenString.split("/");
  const numRows = 8; // Board height
  const numCols = columns.length; // Should be 5

  // Process each column - use exact same logic as ZodiacFEN.decode
  for (let colIndex = 0; colIndex < columns.length; colIndex++) {
    // Start at the top of the board (row 0 in reverse indexing)
    let rowIndex = 0;
    let i = 0;

    while (i < columns[colIndex].length && rowIndex < numRows) {
      const char = columns[colIndex][i];

      // If it's a number, it represents empty squares
      if (!isNaN(parseInt(char))) {
        const emptyCount = parseInt(char);
        rowIndex += emptyCount; // Move down the required number of rows (increasing index in reverse indexing)
        i++;
        continue;
      }

      // Parse pieces one at a time, recognizing piece character boundaries
      let j = i;

      while (
        j < columns[colIndex].length &&
        isNaN(parseInt(columns[colIndex][j])) &&
        columns[colIndex][j] !== "/"
      ) {
        const currentChar = columns[colIndex][j];

        // If this is a '+', skip it (separator)
        if (currentChar === "+") {
          j++;
          continue;
        }

        // Check if this is a piece character
        if (isPieceCharacter(currentChar)) {
          // Start collecting this piece
          let pieceString = currentChar;
          j++;

          // Collect modifiers until we hit another piece character, number, '+', or end
          while (
            j < columns[colIndex].length &&
            isNaN(parseInt(columns[colIndex][j])) &&
            columns[colIndex][j] !== "/" &&
            columns[colIndex][j] !== "+" &&
            !isPieceCharacter(columns[colIndex][j])
          ) {
            pieceString += columns[colIndex][j];
            j++;
          }

          placePieceFromString(
            pieceString,
            { row: rowIndex, col: colIndex },
            gameState,
            engine
          );

          // Move to next row for next piece (unless next char is '+' for same square)
          if (j < columns[colIndex].length && columns[colIndex][j] !== "+") {
            rowIndex++; // Move down (increment index in reverse indexing)
          }
        } else {
          // Unknown character, skip it
          j++;
        }
      }

      i = j;
    }
  }

  gameState.activeBonusTurnPieceId = bonusTurnPieceId;

  // CRITICAL FIX: Rebuild entanglement relationships after loading FEN
  // FEN only stores isEntangled flag on pieces, not the octopus's entangledPieceId
  // This rebuilds the bidirectional relationship for multiplayer sync
  rebuildEntanglementRelationships(gameState);

  // CRITICAL FIX: Rebuild virus infection relationships after loading FEN
  // When a Virus is stacked on an animal via "+V" notation, the animal needs isInfected = true
  // This ensures the virus moves WITH the infected piece when it moves
  rebuildVirusInfectionRelationships(gameState);

  // CRITICAL FIX: Rebuild housing relationships after loading FEN
  // When a climbing piece is housed in a tree via "T+B" notation, the piece needs isHousing = true
  // This ensures captures of housed pieces work correctly in multiplayer
  rebuildHousingRelationships(gameState);

  return gameState;
}

/**
 * Places a piece on the board based on its FEN representation
 * This matches the exact logic from ZodiacFEN.placePiece
 */
function placePieceFromString(
  pieceString: string,
  position: Position,
  gameState: GameState,
  engine: ZodiacEngine
): void {
  if (pieceString.length === 0) return;

  // Extract the base piece character
  const baseChar = pieceString[0];
  const pieceType = getPieceTypeFromChar(baseChar);
  const isLight = baseChar === baseChar.toUpperCase();

  // Create the piece using the engine's methods
  const piece = engine.createPiece(pieceType, isLight, position);

  // Apply special states based on the rest of the string
  if (piece) {
    // FEN LOADING: Set lilypad state for Frogs based on their spawn row
    // Light Frogs on row 0 have lilypad, Dark Frogs on row 7 have lilypad
    // Any other position means they've already moved, so no lilypad
    if (piece.type === PieceType.Frog) {
      const isOnStartingRow = (piece.isLight && position.row === 0) || (!piece.isLight && position.row === 7);

      if (!isOnStartingRow) {
        piece.hasLilyJumped = true;
      } else {
        piece.hasLilyJumped = false;
      }
    }

    // FEN LOADING: Set hasMoved for Rhinos based on position
    // If Rhino is NOT on back rank, it has definitely moved (no lane ability)
    // If Rhino IS on back rank, default to hasMoved = true, but ○ can override to false
    if (piece.type === PieceType.Rhino) {
      const isOnStartingRow = (piece.isLight && position.row === 0) || (!piece.isLight && position.row === 7);

      if (!isOnStartingRow) {
        // Rhino not on back rank = has definitely moved, ○ cannot override this
        piece.hasMoved = true;
      } else {
        // Rhino on back rank = default to moved, but ○ symbol can override to unmoved
        piece.hasMoved = true; // Will be set to false if ○ is present
      }
    }

    // Snake: default hasShed to true (ability already used)
    // ✳️ modifier will override to false if ability is still available
    if (piece.type === PieceType.Snake) {
      piece.hasShed = true;
    }

    for (let i = 1; i < pieceString.length; i++) {
      const specialChar = pieceString[i];

      // Apply special attributes based on the character - matching ZodiacFEN.placePiece exactly
      switch (specialChar) {
        case "\u26D3": // ⛓ Entangled
          piece.isEntangled = true;
          break;
        case "\u2620": // ☠ (poisoned, no VS16)
        case "\u2620\uFE0F": // ☠️ (poisoned, with VS16)
          if (piece.poisonedStatus) {
            piece.poisonedStatus.isPoisoned = true;
          } else {
            piece.poisonedStatus = { isPoisoned: true };
          }
          break;
        case "#": // Meadowed
          piece.isMeadowed = true;
          break;
        case "z": // Zombified (may have turn number suffix, e.g., "z5")
          piece.zombifiedStatus.isZombified = true;
          piece.isZombified = true;
          // Check for following digits (removeOnTurn)
          {
            let zombieTurn = "";
            while (i + 1 < pieceString.length && /\d/.test(pieceString[i + 1])) {
              zombieTurn += pieceString[++i];
            }
            if (zombieTurn) {
              piece.zombifiedStatus.removeOnTurn = parseInt(zombieTurn, 10);
            }
          }
          break;
        case "i": // Infected
          piece.isInfected = true;
          break;
        case "r": // Riding
          piece.isRiding = true;
          break;
        case "h": // Housing
          piece.isHousing = true;
          break;
        case "l": // Lone
          piece.isLone = true;
          break;
        case "d": // Dormant
          piece.isDormant = true;
          break;
        case "$": // Special state
          if (piece.type === PieceType.Wolf) {
            piece.isLone = true;
          } else if (piece.type === PieceType.Octopus) {
            piece.isInfected = true;
          } else if (piece.type === PieceType.Goat) {
            piece.isFainted = true;
          } else if (piece.type === PieceType.Monkey) {
            piece.isRiding = true; // Placeholder for swing when implemented
          }
          break;
        case "K": // Light king (ASCII)
        case "k": // Dark king (ASCII)
        case "\u265B": // ♛ Black Queen symbol (used as king marker)
        case "\u2655": // ♕ White Queen symbol (used as king marker)
          piece.isKing = true;
          break;
        case "*": // Special single-use ability (ASCII)
        case "\u2733": // ✳ Eight Spoked Asterisk (Unicode) - same as *
          if (piece.type === PieceType.Frog) {
            piece.hasLilyJumped = false;
          } else if (piece.type === PieceType.Rhino) {
            piece.hasCharged = false;
          } else if (piece.type === PieceType.Snake) {
            piece.hasShed = false;
          }
          break;
        case "\uFE0F": // Variation Selector-16 (makes previous char emoji) - silently ignore
          break;
        case "○": // Rhino first-move marker (unmoved, lane ability available)
          if (piece.type === PieceType.Rhino) {
            // Only allow ○ to mark unmoved if Rhino is on its starting row
            const rhinoOnStartingRow = (piece.isLight && position.row === 0) || (!piece.isLight && position.row === 7);
            if (rhinoOnStartingRow) {
              piece.hasMoved = false; // Explicitly mark Rhino as unmoved
            }
            // If not on starting row, ignore ○ - Rhino has definitely moved
          }
          break;
        // Direction indicators for Penguin (Unicode arrows and ASCII fallback)
        case "\u2191": // ↑ Up
        case "^":
          if (piece.type === PieceType.Penguin) {
            piece.direction = "up";
          }
          break;
        case "\u2197": // ↗ Up-right
        case "}":
          if (piece.type === PieceType.Penguin) {
            piece.direction = "up-right";
          }
          break;
        case "\u2192": // → Right
        case ">":
          if (piece.type === PieceType.Penguin) {
            piece.direction = "right";
          }
          break;
        case "\u2198": // ↘ Down-right
        case ")":
          if (piece.type === PieceType.Penguin) {
            piece.direction = "down-right";
          }
          break;
        case "\u2193": // ↓ Down
        case "_":
          if (piece.type === PieceType.Penguin) {
            piece.direction = "down";
          }
          break;
        case "\u2199": // ↙ Down-left
        case "(":
          if (piece.type === PieceType.Penguin) {
            piece.direction = "down-left";
          }
          break;
        case "\u2190": // ← Left
        case "<":
          if (piece.type === PieceType.Penguin) {
            piece.direction = "left";
          }
          break;
        case "\u2196": // ↖ Up-left
        case "{":
          if (piece.type === PieceType.Penguin) {
            piece.direction = "up-left";
          }
          break;
        // Additional special characters that might be in FEN
        case "!": // Poisoned (simple marker, no turn number)
          if (piece.poisonedStatus) {
            piece.poisonedStatus.isPoisoned = true;
          } else {
            piece.poisonedStatus = { isPoisoned: true };
          }
          break;
        case "☣": // Poisoned (biohazard symbol, may have turn number suffix)
          if (piece.poisonedStatus) {
            piece.poisonedStatus.isPoisoned = true;
          } else {
            piece.poisonedStatus = { isPoisoned: true };
          }
          // Check for following digits (removeOnTurn)
          {
            let poisonTurn = "";
            while (i + 1 < pieceString.length && /\d/.test(pieceString[i + 1])) {
              poisonTurn += pieceString[++i];
            }
            if (poisonTurn) {
              piece.poisonedStatus.removeOnTurn = parseInt(poisonTurn, 10);
            }
          }
          break;
        case "⚘": // Meadowed (alternative symbol)
          piece.isMeadowed = true;
          break;
        case "☠": // Zombified (alternative symbol, may have turn number suffix)
          piece.zombifiedStatus.isZombified = true;
          piece.isZombified = true;
          // Check for following digits (removeOnTurn)
          {
            let zombieTurn = "";
            while (i + 1 < pieceString.length && /\d/.test(pieceString[i + 1])) {
              zombieTurn += pieceString[++i];
            }
            if (zombieTurn) {
              piece.zombifiedStatus.removeOnTurn = parseInt(zombieTurn, 10);
            }
          }
          break;
        // Ignore unknown characters
        default:
          break;
      }
    }
  }
}

/**
 * Check if a character represents a piece type
 */
function isPieceCharacter(char: string): boolean {
  const upperChar = char.toUpperCase();
  return [
    "B",
    "C",
    "F",
    "G",
    "M",
    "U",
    "O",
    "P",
    "R",
    "S",
    "T",
    "W",
    "V",
  ].includes(upperChar);
}

/**
 * Maps piece characters to PieceType enum values
 */
function getPieceTypeFromChar(char: string): string {
  const upperChar = char.toUpperCase();
  switch (upperChar) {
    case "B":
      return PieceType.Bear;
    case "C":
      return PieceType.Crane;
    case "F":
      return PieceType.Frog;
    case "G":
      return PieceType.Goat;
    case "M":
      return PieceType.Monkey;
    case "U":
      return PieceType.Fungi;
    case "O":
      return PieceType.Octopus;
    case "P":
      return PieceType.Penguin;
    case "R":
      return PieceType.Rhino;
    case "S":
      return PieceType.Snake;
    case "T":
      return PieceType.Tree;
    case "W":
      return PieceType.Wolf;
    case "V":
      return PieceType.Virus;
    default:
      return PieceType.Bear; // Default fallback
  }
}

export function loadTestScenarioFEN(
  engine: ZodiacEngine,
  fen: string,
  preserveTurnCount: boolean = false
) {
  console.clear(); // Clear the log for a decluttered test each time

  // Always clear the board first to avoid stacking pieces
  if ((engine as any).gameStateManager) {
    (engine as any).gameStateManager.emptyBoard();
  }
  // FEN/move history is managed in the UI (FENHistory component) and resets on game mode/gameId/turnCount change.
  // No need to reset here; if needed, trigger a UI/context update instead.
  // Decode the FEN into a new game state using our custom parser for test scenarios
  const newGameState = FenToGameState(fen, engine);

  // Handle turn count based on the preserveTurnCount parameter
  if (newGameState) {
    if (preserveTurnCount) {
      // Preserve existing turn count for tutorial overrides
      const currentGameState = engine.getGameState();
      newGameState.turnCount = currentGameState.turnCount;
    } else {
      // Reset turn count for new scenario (default behavior)
      newGameState.turnCount = 0;
    }
  }
  // Set king types based on pieces marked as isKing
  if (newGameState) {
    let foundLightKingType: string | null = null;
    let foundDarkKingType: string | null = null;
    for (let row = 0; row < newGameState.board.length; row++) {
      for (let col = 0; col < newGameState.board[row].length; col++) {
        for (const piece of newGameState.board[row][col].Pieces) {
          if (piece.isKing) {
            if (piece.isLight) foundLightKingType = piece.type;
            else foundDarkKingType = piece.type;
          }
        }
      }
    }
    if (foundLightKingType)
      newGameState.lightKing =
        (PieceType as any)[foundLightKingType] || foundLightKingType;
    if (foundDarkKingType)
      newGameState.darkKing =
        (PieceType as any)[foundDarkKingType] || foundDarkKingType;
  }
  // Overwrite the current game state if manager exists
  if ((engine as any).gameStateManager) {
    (engine as any).gameStateManager.setGameState(newGameState);
    // Reset repeatString for new test scenario
    (engine as any).gameStateManager.getGameState().repeatString = "";
    // CRITICAL FIX: Reset FEN history on the REAL stored state to prevent stale history
    // engine.getGameState() returns shallow copies, so fenHistory must be cleared on
    // the actual gameStateManager state to prevent false threefold repetition draws
    const realState = (engine as any).gameStateManager.getGameState();
    if (engine.fenHistoryManager) {
      engine.fenHistoryManager.initializeWithCurrentState(realState);
    } else {
      realState.fenHistory = [];
    }
  }
  if (newGameState) {
    // Set mode and state
    newGameState.mode = Mode.TestAI;
    newGameState.state = State.Playing;
    newGameState.lightWon = undefined;
    // BUG FIX: Preserve turn from FEN (already set by FenToGameState based on 'd' prefix)
    // DO NOT force Light's turn - this breaks Dark AI tests!
    // newGameState.isLightTurn is already set correctly by FenToGameState
    // Clear any existing tutorial failure flags from previous games
    (newGameState as any).tutorialFailed = false;
  }
  // Ensure AI controller is initialized
  if (!engine.aiController) {
    engine.aiController = new AIController(engine);
    if (engine.aiController) {
      engine.aiController.setSearchIterations(500);
    }
  }
  // Notify listeners
  if ((engine as any).eventManager) {
    (engine as any).eventManager.notifyLocalListeners();
  }
  // FINAL: Force mode to TestAI in the current game state to prevent accidental overwrite
  if ((engine as any).gameStateManager) {
    const state = (engine as any).gameStateManager.getGameState();
    state.mode = Mode.TestAI;

    // TUTORIAL SUPPORT: Only set tutorial configs when actually in Tutorial mode
    const existingTutorialConfig = (state as any).tutorialConfig;

    if (existingTutorialConfig) {
    } else if (state.mode === Mode.Tutorial) {
      // Only attempt tutorial pattern detection when in actual Tutorial mode

      if (fen.includes("CK1T") || fen.includes("T+C") || fen.includes("TC")) {
        (state as any).tutorialConfig = {
          constellation: "Tree",
          tutorialIndex: 1, // Tree Tutorial 2 (0-based indexing)
          uiLogger: () => {},
        };
      } else {
      }
    } else {
    }

  }
}
